import React from 'react';
import { getBillingFormattedDate, getFormattedDate, parseToDate } from '../../../utils/date';
import getDeviceStatusLabel from './getDeviceStatusLabel';
const getDeviceTimestamp = (status, timestamp) => {
    if (!timestamp) {
        return 'N/A';
    }
    if (status === 'ACTIVATED' || status === 'CANCELLED') {
        return getBillingFormattedDate(timestamp, 'MMM d, yyyy');
    }
    return getFormattedDate(parseToDate(timestamp), 'MMM d, yyyy');
};
const formatDeliveryDate = (date, label) => ({
    label,
    value: getFormattedDate(parseToDate(date), 'MMM d, yyyy (HH:mm)')
});
const createFulfillmentDetails = (fulfillmentDetails) => {
    if (!fulfillmentDetails?.length) {
        return [];
    }
    const firstFulfillment = fulfillmentDetails[0];
    const details = [];
    // Always add serial number
    if (firstFulfillment.device_serial) {
        details.push({
            label: 'Serial',
            value: firstFulfillment.device_serial
        });
    }
    // Add tracking information if available
    if (firstFulfillment.tracking_number) {
        details.push({
            label: 'Tracking',
            value: firstFulfillment.tracking_number,
            url: firstFulfillment.tracking_url
        });
    }
    // Add delivery information (prioritize delivered_at over expected)
    if (firstFulfillment.delivered_at) {
        details.push(formatDeliveryDate(firstFulfillment.delivered_at, 'Delivered at'));
    }
    else if (firstFulfillment.delivery_expected_at) {
        details.push(formatDeliveryDate(firstFulfillment.delivery_expected_at, 'Expected delivery at'));
    }
    return details;
};
const createExchangeDetails = (deviceStatus) => {
    const replacementDetails = [];
    const returnDetails = [];
    // Replacement device details
    if (deviceStatus.exchange_fulfillment_details?.length) {
        const exchangeFulfillment = deviceStatus.exchange_fulfillment_details[0];
        if (exchangeFulfillment.device_serial) {
            replacementDetails.push({
                label: 'Serial',
                value: exchangeFulfillment.device_serial
            });
        }
        if (exchangeFulfillment.tracking_number) {
            replacementDetails.push({
                label: 'Tracking',
                value: exchangeFulfillment.tracking_number,
                url: exchangeFulfillment.tracking_url
            });
        }
        // Add delivery information for replacement
        if (exchangeFulfillment.delivered_at) {
            replacementDetails.push(formatDeliveryDate(exchangeFulfillment.delivered_at, 'Delivered at'));
        }
        else if (exchangeFulfillment.delivery_expected_at) {
            replacementDetails.push(formatDeliveryDate(exchangeFulfillment.delivery_expected_at, 'Expected delivery at'));
        }
    }
    // Return device details
    if (deviceStatus.return_serial) {
        returnDetails.push({
            label: 'Serial',
            value: deviceStatus.return_serial
        });
    }
    if (deviceStatus.return_tracking_number) {
        returnDetails.push({
            label: 'Tracking',
            value: deviceStatus.return_tracking_number
        });
    }
    if (deviceStatus.return_label_url) {
        returnDetails.push({
            label: 'Return label',
            value: 'Download',
            url: deviceStatus.return_label_url
        });
    }
    return [replacementDetails, returnDetails];
};
const getDeviceStatusTitle = (deviceStatus) => {
    const isOrderedWithoutTracking = deviceStatus.device_status === 'ORDERED' &&
        !deviceStatus.fulfillment_details?.[0]?.tracking_number;
    return isOrderedWithoutTracking
        ? 'Ordered (not shipped)'
        : getDeviceStatusLabel(deviceStatus.device_status);
};
export const usePreparedDeviceStatusLogs = (deviceStatuses) => {
    return React.useMemo(() => {
        if (!deviceStatuses) {
            return [];
        }
        return deviceStatuses
            .filter((deviceStatus) => deviceStatus.status_timestamp !== null)
            .map((deviceStatus) => {
            if (deviceStatus.device_status === 'EXCHANGED' ||
                deviceStatus.device_status === 'CANCELLED') {
                const exchangeDetails = createExchangeDetails(deviceStatus);
                const hasUnreturnedDevice = deviceStatus.return_status?.toLowerCase() !== 'returned' ||
                    deviceStatus.is_returned === false;
                return {
                    title: getDeviceStatusTitle(deviceStatus),
                    timestamp: getDeviceTimestamp(deviceStatus.device_status, deviceStatus.status_timestamp),
                    alert: hasUnreturnedDevice,
                    ...(exchangeDetails.length > 0 && {
                        details: exchangeDetails
                    })
                };
            }
            // For other statuses, use regular fulfillment details
            const fulfillmentDetails = createFulfillmentDetails(deviceStatus.fulfillment_details);
            return {
                title: getDeviceStatusTitle(deviceStatus),
                timestamp: getDeviceTimestamp(deviceStatus.device_status, deviceStatus.status_timestamp),
                ...(fulfillmentDetails.length > 0 && {
                    details: [fulfillmentDetails]
                })
            };
        });
    }, [deviceStatuses]);
};
